import type { EntityAdapter, EntityState } from '@reduxjs/toolkit';
import type { EndpointBuilder, QueryDefinition } from '@reduxjs/toolkit/query';
import {
  logger,
  UpdateActionEnum,
  wsWaitForAllPages,
  type RequestStream,
  type SubscriptionResponse,
} from '@talos/kyoko';
import { getSubscriptionErrorMessage } from '@talos/kyoko/src/types/SubscriptionResponse';
import type { Draft } from 'immer';
import { combineLatest, distinctUntilChanged, firstValueFrom, map, share, startWith, Subject } from 'rxjs';
import { v1 as uuid } from 'uuid';
import type { AppExtraArgument } from './types';

/**
 * Extracts the UpdateAction from an item, falling back to the response-level action.
 */
export function getUpdateAction(
  item: { UpdateAction?: UpdateActionEnum },
  fallbackAction: UpdateActionEnum | undefined
): UpdateActionEnum | undefined {
  return item.UpdateAction ?? fallbackAction;
}

/**
 * Extracts error message from RTK Query cache entry error.
 * Handles:
 * - WebSocket subscription errors (via getSubscriptionErrorMessage)
 * - RTK Query SerializedError: { message: string }
 */
export function getErrorMessage(error: unknown): string {
  if (typeof error === 'string') {
    return error;
  }
  // Try subscription response error first
  const subscriptionError = getSubscriptionErrorMessage(error);
  if (subscriptionError !== undefined) {
    return subscriptionError;
  }
  // RTK Query SerializedError has a 'message' property
  if (error && typeof error === 'object' && 'message' in error && typeof error.message === 'string') {
    return error.message;
  }
  return 'An unknown error occurred.';
}

/**
 * A factory function that builds a query definition for a WebSocket endpoint, and wires up the necessary
 * logic to handle the subscription.
 * @param builder
 * @see https://redux-toolkit.js.org/rtk-query/usage/streaming-updates
 */
export function buildWsEndpointBuilder<Builder extends EndpointBuilder<any, any, any>>(
  builder: Builder,
  {
    pagingUniqueKeyCallback,
  }: {
    pagingUniqueKeyCallback?: <T>(data: T) => string;
  } = {}
) {
  /**
   * Map of cache keys to promises that resolve when the initial data is received
   * This is used to ensure that the `isLoading` state is correct.
   *
   * If the `queryFn` resolve immediately, the `isLoading` state would be `true`, even though
   * we haven't received the initial WebSocket data yet.
   */
  const promises = new Map<
    CacheKey,
    {
      resolve: (value: QueryReturnValue<any, any, any>) => void;
      reject: (reason?: unknown) => void;
      promise: Promise<QueryReturnValue<any, any, any>>;
    }
  >();

  /**
   * Sometimes the `queryFn` is called first (for example on retries, or force fetches),
   * and sometimes the `onCacheEntryAdded` is called first (when the cache entry is added).
   *
   * This little utility help us cater to both cases.
   */
  function getOrCreatePromise(cacheKey: CacheKey) {
    if (promises.has(cacheKey)) {
      return promises.get(cacheKey)!;
    }
    const { promise, resolve, reject } = Promise.withResolvers<QueryReturnValue<any, any, any>>();
    promises.set(cacheKey, { promise, resolve, reject });
    return { promise, resolve, reject };
  }

  /**
   * Generate a consistent cache key that includes the request name
   * and the serialized query args.
   *
   * If serializedQueryArgs is customized, it will be used to generate the cache key - to allow for custom cache conflict resolution.
   * Otherwise, the default serialization will be used.
   *
   * TODO: Make a change to the overall process to allow for endpointName to distinguish unique cache keys
   */
  function generateCacheKey<TQuery>(
    queryArgs: TQuery,
    buildRequest: (args: TQuery) => RequestStream,
    serializeQueryArgs: (args: TQuery) => string
  ): string {
    const serializedArgs = serializeQueryArgs(queryArgs);
    let parsedArgs = {};
    if (serializedArgs) {
      try {
        parsedArgs = JSON.parse(serializedArgs);
      } catch (e) {
        logger.error(new Error('Failed to parse serializedArgs in generateCacheKey:'), {
          extra: { serializedArgs, error: e },
        });
        parsedArgs = {};
      }
    }

    // Extract stable properties instead of using fragile function.toString()
    // Call buildRequest with a safe default to extract stable properties
    const defaultArgs = {} as TQuery;
    const requestSample = buildRequest(defaultArgs);
    return JSON.stringify({
      ...parsedArgs,
      requestName: requestSample.name,
    });
  }

  /**
   * Loosely resembles the default createApi#query function, but with a narrower API surface.
   *
   * @see https://redux-toolkit.js.org/rtk-query/api/createApi#query
   */
  function query<TResult, TQuery>(
    definition: WsQueryEndpointDefinition<TQuery, TResult>
  ): QueryDefinition<TQuery, any, any, TResult, ExtractReducerPath<Builder>> {
    const { buildRequest, serializeQueryArgs = defaultSerializeWsQueryArgs, ...args } = definition;

    return builder.query<TResult, TQuery>({
      /**
       * The queryFn controls the `isFetching` and `isLoading` state.
       * As we know, WebSocket subscriptions doesn't really operate on a request/response basis,
       * so we mimic that behavior by returning a promise that resolves when the initial data is received.
       */
      queryFn(args): Promise<QueryReturnValue<TResult, any, any>> {
        const cacheKey = generateCacheKey(args, buildRequest, serializeQueryArgs);
        const { promise } = getOrCreatePromise(cacheKey);
        return promise;
      },
      serializeQueryArgs: ({ queryArgs }) => generateCacheKey(queryArgs, buildRequest, serializeQueryArgs),

      /**
       * This is called each time a new cache _key_ (not the value) is added to the cache.
       */
      async onCacheEntryAdded(args, api) {
        const { updateCachedData, cacheEntryRemoved } = api;
        const cacheKey = generateCacheKey(args, buildRequest, serializeQueryArgs);

        const promiseEntry = getOrCreatePromise(cacheKey);
        const firstDataLoaded = promiseEntry;

        // Set up WS subscription
        const address = uuid();
        const request = buildRequest(args);
        const messageCallback: Parameters<typeof wsClient.registerSubscription>[2] = (err: any, json) => {
          if (err != null) {
            firstDataLoaded.resolve({ error: 'error' in err ? err.error?.msg : 'An unknown error occurred.' });
            return;
          }

          if (json == null) {
            firstDataLoaded.resolve({ error: 'An unknown error occurred.' });
            logger.error(new Error('Received empty response from WebSocket'), { extra: { address, request } });
            return;
          }

          if (json.initial) {
            if ('entityAdapter' in definition) {
              firstDataLoaded.resolve({
                data: definition.entityAdapter.addMany(definition.entityAdapter.getInitialState(), json.data),
              });
            } else {
              firstDataLoaded.resolve({ data: definition.initializeCache(json.data, json) });
            }
            promises.delete(cacheKey);
          } else {
            updateCachedData(draft => {
              if ('entityAdapter' in definition) {
                definition.entityAdapter.upsertMany(draft as Draft<any>, json.data);
              } else if ('removeSupport' in definition && definition.removeSupport) {
                // Handle removals for Map-based caches with removeSupport
                const { getUniqueKey } = definition.removeSupport;
                const map = draft as Map<unknown, unknown>;

                const itemsToProcess: typeof json.data = [];
                for (const item of json.data) {
                  const updateAction = getUpdateAction(item as { UpdateAction?: UpdateActionEnum }, json.action);
                  if (updateAction === UpdateActionEnum.Remove) {
                    const key = getUniqueKey(item);
                    if (key !== undefined) {
                      map.delete(key);
                    }
                  } else {
                    itemsToProcess.push(item);
                  }
                }

                if (itemsToProcess.length > 0) {
                  definition.updateCache(draft, itemsToProcess, json);
                }
              } else {
                definition.updateCache(draft, json.data, json);
              }
            });
          }
        };

        const extra = api.extra as AppExtraArgument;
        const wsClient = extra.wsClient;

        // Create a subject to process callback data
        const callbackSubject = new Subject<{ err: any; json: any }>();

        // Share the original stream so both branches see the same items
        const shared$ = callbackSubject.pipe(share());

        // Branch 1: extract the json for wsWaitForAllPages
        const json$ = shared$.pipe(
          map(item => item.json),
          pagingUniqueKeyCallback
            ? wsWaitForAllPages({
                getUniqueKey: pagingUniqueKeyCallback,
              })
            : map(item => item)
        );

        // Branch 2: extract the error value, starting with null if none has come through
        const error$ = shared$.pipe(
          map(item => item.err),
          startWith(undefined),
          distinctUntilChanged()
        );

        // Combine the results so that the final output includes both
        const subjectSubscription = combineLatest([json$, error$])
          .pipe(map(([json, err]) => ({ err, json })))
          .subscribe(({ err, json }: { err: unknown; json: unknown }) => {
            messageCallback(err, json as SubscriptionResponse<unknown>);
          });

        if (!wsClient.isConnected) {
          // Wait until `onOpen` has emitted at least one value
          await firstValueFrom(wsClient.onOpen);
        }
        const unsubscribe = wsClient.registerSubscription(address, [request], (err: unknown, json: unknown) => {
          callbackSubject.next({ err, json });
        });

        try {
          await cacheEntryRemoved;
          promises.delete(cacheKey);
        } finally {
          unsubscribe();
          subjectSubscription.unsubscribe();
          callbackSubject.unsubscribe();
        }
      },
      ...args,
    });
  }

  return { query };
}

/**
 * Default serialization that just serializes the args as-is.
 * The endpoint hash system ensures uniqueness between different endpoints.
 */
export function defaultSerializeWsQueryArgs(args: unknown) {
  return JSON.stringify(args);
}

// Public types
export type WsQueryEndpointBaseDefinition<TQuery> = {
  /** Construct the WebSocket request (subscription) stream object */
  buildRequest: (args: TQuery) => RequestStream;
  /** Serialize the query args to a cache key. */
  serializeQueryArgs?: (args: TQuery) => string;
  /** Optionally specify how long to keep unused data in the cache. */
  keepUnusedDataFor?: number;
};

export type WsQueryEndpointDefinition<TQuery, TResult> =
  /**
   * Provide an `entityAdapter` that magically updates the state
   * for you (assuming the data is an array of entities).
   */

  | (WsQueryEndpointBaseDefinition<TQuery> & {
      entityAdapter: RequireEntityAdapter<TResult>;
    })

  /** If you need more granular control over the cache initialization, use this. */
  | (WsQueryEndpointBaseDefinition<TQuery> & {
      /** Initialize the cache with the data received from the WebSocket. */
      initializeCache: <TData extends any[]>(data: TData, response: SubscriptionResponse<TData[number]>) => TResult;
      /** Handle updates coming from the WebSocket. */
      updateCache: RecipeWithData<TResult>;
      /**
       * Optional remove support for Map-based caches.
       * When provided, items with UpdateAction.Remove will be automatically deleted from the cache
       * before calling updateCache with the remaining items.  This is equivalent behavior to wsScanToMap for WebSocket subscriptions.
       */
      removeSupport?: {
        /** Function to extract the unique key from each item for cache operations */
        getUniqueKey: (item: any) => string | number;
      };
    });

type RequireEntityAdapter<T> = T extends EntityState<infer Entity, infer ID> ? EntityAdapter<Entity, ID> : never;

type ExtractReducerPath<Builder> = Builder extends EndpointBuilder<any, any, infer R> ? R : never;
type CacheKey = string | number | boolean | Record<any, any>;
type MaybeDrafted<T> = T | Draft<T>;
type RecipeWithData<TState, TData = any> = (
  draft: MaybeDrafted<TState>,
  data: TData[],
  response: SubscriptionResponse<TData>
) => void | MaybeDrafted<TState>;
export type QueryReturnValue<TData = unknown, TError = unknown, TMeta = unknown> =
  | {
      error: TError;
      data?: undefined;
      meta?: TMeta;
    }
  | {
      error?: undefined;
      data: TData;
      meta?: TMeta;
    };
